import classNames from 'classnames';
import {
  format,
  getDay,
  getWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import _ from 'lodash';
import React from 'react';
import { TwTheme } from '../../../Tw';
import useGetActualityAggregationMetricValue from '../hooks/useGetActualityAggregationMetricValue';

const colors = TwTheme.extend.colors;

export type ChartHeaderLevelType =
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year'
  | 'custom';

export interface ChartHeaderLevelConfig {
  type: ChartHeaderLevelType;
  className?: string;
  style?: React.CSSProperties;
  labelAlign?: 'start' | 'center' | 'end';
  // Custom formatter function for labels - now supports React components
  formatter?: (tick: Date, allTicks: Date[]) => string | React.ReactNode;
  // For custom levels, specify which time components to group by
  groupBy?: (tick: Date) => string;
  // Custom span calculator
  spanCalculator?: (tick: Date, allTicks: Date[]) => number;
  // Status-based styling
  hasIssue?: boolean;
  isWarning?: boolean;
  isActive?: boolean;
  // Make text sticky for horizontal scrolling
  stickyText?: boolean;
}

interface TimeLevel {
  type: ChartHeaderLevelType;
  label: React.ReactNode; // <-- allow string or JSX
  value: Date | string;
  span: number; // How many ticks this spans
  isWeekend?: boolean;
  isNightShift?: boolean;
  // Pass through status from config
  hasIssue?: boolean;
  isWarning?: boolean;
  isActive?: boolean;
  stickyText?: boolean;
}

interface ChartHeaderProps {
  ticks: Date[];
  position?: 'top' | 'bottom';
  sticky?: boolean;
  absolute?: boolean;
  showShiftLayers?: boolean;
  showWeekendHighlight?: boolean;
  granularity:
    | ChartHeaderLevelType
    | 'eventHour'
    | 'eventDay'
    | 'eventWeek'
    | 'eventMonth';
  leftOffset?: string;
  rightOffset?: string;
  className?: string;
  levelsConfig?: ChartHeaderLevelConfig[];
  skipEmptyLevels?: boolean;
  labelAlign?: 'start' | 'center' | 'end';
}

const ChartHeader: React.FC<ChartHeaderProps> = ({
  ticks,
  position = 'top',
  sticky = true,
  absolute = false,
  showShiftLayers = true,
  showWeekendHighlight = true,
  granularity,
  leftOffset = '150px',
  rightOffset = '40px',
  className,
  levelsConfig,
  skipEmptyLevels = true,
  labelAlign = 'center',
}) => {
  const getMetricValue = useGetActualityAggregationMetricValue();

  // Helper to generate levels based on config
  const generateLevelFromConfig = (
    config: ChartHeaderLevelConfig,
    ticks: Date[],
  ): TimeLevel[] => {
    if (config.type === 'custom') {
      // Handle custom levels with groupBy function
      if (config.groupBy) {
        const groups = _.groupBy(ticks, config.groupBy);
        return _.map(groups, (groupTicks, groupKey) => ({
          type: 'custom' as const,
          label: config.formatter
            ? config.formatter(groupTicks[0], ticks)
            : String(groupKey),
          value: groupTicks[0],
          span: config.spanCalculator
            ? config.spanCalculator(groupTicks[0], ticks)
            : groupTicks.length,
          // Pass through status properties
          hasIssue: config.hasIssue,
          isWarning: config.isWarning,
          isActive: config.isActive,
          stickyText: config.stickyText,
        }));
      }
      // If no groupBy, treat each tick as its own level
      return ticks.map(tick => ({
        type: 'custom',
        label: config.formatter
          ? config.formatter(tick, ticks)
          : tick.toString(),
        value: tick,
        span: config.spanCalculator ? config.spanCalculator(tick, ticks) : 1,
        // Pass through status properties
        hasIssue: config.hasIssue,
        isWarning: config.isWarning,
        isActive: config.isActive,
        stickyText: config.stickyText,
      }));
    }

    // Use existing generators for standard types
    const generator = levelGenerators[config.type];
    if (!generator) return [];

    const standardLevels = generator(ticks);

    // Apply custom formatter and pass through status properties
    return standardLevels.map(level => ({
      ...level,
      label: config.formatter
        ? config.formatter(
            level.value instanceof Date && !isNaN(level.value.getTime())
              ? level.value
              : new Date(0), // or fallback to String(level.value)
            ticks,
          )
        : level.label,
      // Pass through status properties
      hasIssue: config.hasIssue,
      isWarning: config.isWarning,
      isActive: config.isActive,
      stickyText: config.stickyText,
    }));
  };

  // Helper to generate all possible levels
  const levelGenerators: Record<
    Exclude<ChartHeaderLevelType, 'custom'>,
    (ticks: Date[]) => TimeLevel[]
  > = {
    hour: (ticks: Date[]) => {
      return ticks.map(tick => {
        if (tick instanceof Date && !isNaN(tick.getTime())) {
          return {
            type: 'hour',
            label: getMetricValue(tick as any)?.value || String(tick),
            value: tick,
            span: 1,
            isWeekend:
              showWeekendHighlight &&
              (getDay(tick) === 0 || getDay(tick) === 6),
            isNightShift:
              showShiftLayers && (tick.getHours() >= 22 || tick.getHours() < 6),
          };
        }
        // fallback for non-date
        return {
          type: 'hour',
          label: String(tick),
          value: tick,
          span: 1,
          isWeekend: false,
          isNightShift: false,
        };
      });
    },
    day: (ticks: Date[]) => {
      const dayGroups = _.groupBy(ticks, tick => {
        if (tick instanceof Date && !isNaN(tick.getTime())) {
          return format(startOfDay(tick), 'yyyy-MM-dd');
        }
        return String(tick); // fallback for non-date
      });
      return _.map(dayGroups, (dayTicks, dayStr) => {
        const firstValid = dayTicks.find(
          t => t instanceof Date && !isNaN(t.getTime()),
        );
        return {
          type: 'day',
          label: firstValid ? (
            <span className="flex items-center gap-1 px-2">
              <span
                className={classNames(
                  'rounded px-1 py-0.5',
                  'border-menu-text/40 border',
                  getDay(firstValid) === 0 ||
                    (getDay(firstValid) === 6 && 'border-menu-text/20'),
                )}
              >
                {format(firstValid, 'EEE')}
              </span>
              <span className="text-menu-text text-sm">
                {format(firstValid, 'dd')}
              </span>
            </span>
          ) : (
            String(dayStr)
          ),
          value: firstValid ?? new Date(0),
          span: dayTicks.length,
          isWeekend: firstValid
            ? getDay(firstValid) === 0 || getDay(firstValid) === 6
            : false,
        };
      });
    },
    week: (ticks: Date[]) => {
      const weekGroups = _.groupBy(ticks, tick =>
        format(startOfWeek(tick, { weekStartsOn: 1 }), 'yyyy-ww'),
      );
      return _.map(weekGroups, (weekTicks, weekStr) => {
        const weekStart = startOfWeek(weekTicks[0], { weekStartsOn: 1 });
        return {
          type: 'week',
          label: `W${getWeek(weekStart)}`,
          value: weekStart,
          span: weekTicks.length,
        };
      });
    },
    month: (ticks: Date[]) => {
      const monthGroups = _.groupBy(ticks, tick =>
        format(startOfMonth(tick), 'yyyy-MM'),
      );
      return _.map(monthGroups, (monthTicks, monthStr) => ({
        type: 'month',
        label: format(monthTicks[0], 'MMM'),
        value: startOfMonth(monthTicks[0]),
        span: monthTicks.length,
      }));
    },
    year: (ticks: Date[]) => {
      const yearGroups = _.groupBy(ticks, tick =>
        format(startOfYear(tick), 'yyyy'),
      );
      return _.map(yearGroups, (yearTicks, yearStr) => ({
        type: 'year',
        label: format(yearTicks[0], 'yyyy'),
        value: startOfYear(yearTicks[0]),
        span: yearTicks.length,
      }));
    },
  };

  // Default level order if not provided
  const defaultLevelOrder: ChartHeaderLevelConfig[] = (() => {
    if (granularity === 'eventHour' || granularity === 'hour') {
      return [
        { type: 'hour', labelAlign: 'center' },
        { type: 'day', labelAlign: 'start' },
        { type: 'week', labelAlign: 'start' },
        { type: 'month', labelAlign: 'start' },
        { type: 'year', labelAlign: 'start' },
      ];
    }
    if (granularity === 'eventDay' || granularity === 'day') {
      return [
        { type: 'day', labelAlign: 'center' },
        { type: 'week', labelAlign: 'start' },
        { type: 'month', labelAlign: 'start' },
        { type: 'year', labelAlign: 'start' },
      ];
    }
    if (granularity === 'eventWeek' || granularity === 'week') {
      return [
        { type: 'week', labelAlign: 'start' },
        { type: 'month', labelAlign: 'start' },
        { type: 'year', labelAlign: 'start' },
      ];
    }
    if (granularity === 'eventMonth' || granularity === 'month') {
      return [
        { type: 'month', labelAlign: 'center' },
        { type: 'year', labelAlign: 'start' },
      ];
    }
    return [
      { type: 'hour', labelAlign: 'center' },
      { type: 'day', labelAlign: 'start' },
      { type: 'week', labelAlign: 'start' },
      { type: 'month', labelAlign: 'start' },
      { type: 'year', labelAlign: 'start' },
    ];
  })();

  // Build the levels to render
  const levelsToRender: ChartHeaderLevelConfig[] =
    levelsConfig || defaultLevelOrder;

  // Generate the time hierarchy for the selected levels
  const timeHierarchy: TimeLevel[][] = levelsToRender
    .map(levelCfg => generateLevelFromConfig(levelCfg, ticks))
    .filter(level => !skipEmptyLevels || level.length > 0); // Skip empty levels if enabled

  // Update levelsToRender to match filtered hierarchy
  const filteredLevelsToRender = levelsToRender.filter((_, index) => {
    const level = levelsToRender.map(levelCfg =>
      generateLevelFromConfig(levelCfg, ticks),
    )[index];
    return !skipEmptyLevels || level.length > 0;
  });

  const levelHeight = 32; // Height of each level in pixels

  const getBackgroundPattern = (tick: Date, index: number): string => {
    const baseClasses = [];

    // Alternating background
    if (index % 2 === 0) {
      baseClasses.push('bg-menu/10');
    } else {
      baseClasses.push('bg-menu/5');
    }

    // Weekend highlighting
    if (
      showWeekendHighlight &&
      tick instanceof Date &&
      !isNaN(tick.getTime()) &&
      (getDay(tick) === 0 || getDay(tick) === 6)
    ) {
      baseClasses.push('bg-app-panel-dark/20');
    }

    // Night shift highlighting
    if (
      showShiftLayers &&
      (granularity === 'eventHour' || granularity === 'hour') &&
      tick instanceof Date &&
      !isNaN(tick.getTime()) &&
      (tick.getHours() >= 22 || tick.getHours() < 6)
    ) {
      baseClasses.push('bg-menu/50');
    }

    return classNames(baseClasses);
  };

  const getPositionClasses = () => {
    if (absolute) {
      return position === 'top'
        ? 'absolute top-0 left-0 right-0'
        : 'absolute bottom-0 left-0 right-0';
    }
    if (sticky) {
      return position === 'top' ? 'sticky top-0' : 'sticky bottom-0';
    }
    return '';
  };

  const getLabelAlignClasses = (levelAlign?: 'start' | 'center' | 'end') => {
    const align = levelAlign || labelAlign;
    switch (align) {
      case 'start':
        return `justify-start items-center text-center flex-1 sticky left-[150px]`;
      case 'end':
        return `justify-end items-center text-center flex-1 sticky right-[150px]`;
      case 'center':
      default:
        return 'justify-center items-center text-center flex-1';
    }
  };

  const totalHeight = filteredLevelsToRender.length * levelHeight;

  if (_.isEmpty(ticks) || timeHierarchy.length === 0) {
    return null;
  }

  return (
    <div
      data-component="ChartHeader"
      className={classNames(
        'z-10 flex flex-col',

        // 'h-full',
        // getPositionClasses(),
        className,
      )}
      // style={{ height: `${totalHeight}px` }}
    >
      {/* Background pattern layer */}
      <div
        className="inset-0 bottom-0 top-0 z-0 flex flex-row"
        style={{
          marginLeft: leftOffset,
          marginRight: rightOffset,
        }}
      >
        {_.map(ticks, (tick, index) => (
          <div
            data-component={`ChartHeaderItem-${index}`}
            key={
              tick && typeof tick.toString === 'function'
                ? tick.toString()
                : `tick-${index}`
            }
            className={classNames(
              'truncate',
              'z-0',
              'flex-1',
              'text-center',
              getBackgroundPattern(tick, index),
            )}
            style={{ boxSizing: 'border-box' }}
          />
        ))}
      </div>

      {/* Time hierarchy levels */}
      {_.map(timeHierarchy, (level, levelIndex) => {
        const levelCfg = filteredLevelsToRender[levelIndex];
        return (
          <div
            data-component={`ChartHeaderLevel-${levelIndex}`}
            key={levelIndex}
            className={classNames(
              'relative flex gap-px',
              'shadow-2xl',
              'h-full',
              'w-full',
              // 'bg-menu/40',
              levelCfg?.className,
            )}
            style={{
              // height: `${levelHeight}px`,
              paddingLeft: leftOffset,
              paddingRight: rightOffset,
              ...levelCfg?.style,
            }}
          >
            {_.map(level, (timeLevel, itemIndex) => {
              const widthPercentage = (timeLevel.span / ticks.length) * 100;

              return (
                <div
                  data-component={`ChartHeaderItem-${levelIndex}-${itemIndex}`}
                  key={`${levelIndex}-${itemIndex}`}
                  className={classNames(
                    'flex',
                    'text-xs font-medium',
                    'truncate',
                    'p-2',
                    'border-menu border-l',
                    'relative', // For sticky text positioning
                    'bg-app-panel-dark/20 backdrop-blur-xl',
                    getLabelAlignClasses(levelCfg?.labelAlign),
                    {
                      // Weekend/Night shift highlighting
                      'bg-app-panel-dark/30':
                        timeLevel.isWeekend && showWeekendHighlight,
                      'bg-app-panel-dark/10':
                        timeLevel.isNightShift && showShiftLayers,
                      'opacity-50': timeLevel.isWeekend && showWeekendHighlight,
                      'text-menu-text/50':
                        timeLevel.isNightShift && showShiftLayers,

                      // Status-based styling
                      // 'bg-issue-transparent bg-app-panel-dark':
                      //   timeLevel.hasIssue,
                      // 'bg-warning-transparent/40 border-warning':
                      //   timeLevel.isWarning,
                      // 'bg-brand/20 border-brand ring-brand/30 ring-1':
                      //   timeLevel.isActive,
                    },
                  )}
                  style={{
                    flex: `${timeLevel.span} 0 0`,
                  }}
                  title={`${timeLevel.type}: ${timeLevel.label}`}
                >
                  {timeLevel.stickyText
                    ? // Sticky text implementation
                      // <div
                      //   title={timeLevel.label.toString()}
                      //   className="sticky left-2 z-10 truncate rounded px-1 shadow-sm"
                      // >
                      timeLevel.label
                    : // </div>
                      timeLevel.label}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default ChartHeader;
